import React, { useState, useMemo } from 'react';
import { useWaterStore } from '../store/useWaterStore';
import { useAuthStore } from '../store/useAuthStore';
import { getLocalDateString } from '../utils/formatDate';
import { ChevronLeft, ChevronRight, BarChart2, Calendar as CalendarIcon, Lock } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
 CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

export const MonthlyStats: React.FC = () => {
  const { monthlyStats, dailyGoal } = useWaterStore();
  const { isGuest } = useAuthStore();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'charts'>('charts');

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Prepare data for charts
  const chartData = useMemo(() => {
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const data = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dateStr = getLocalDateString(date);
      const intake = monthlyStats[dateStr] || 0;
      data.push({
        day: day,
        date: dateStr,
        intake: Math.round(intake), // ml
        goal: dailyGoal,
      });
    }
    return data;
 }, [currentDate, monthlyStats, dailyGoal]);

  const weeklyData = useMemo(() => {
     // Get last 7 days
     const data = [];
     for (let i = 6; i >= 0; i--) {
       const d = new Date();
       d.setDate(d.getDate() - i);
       const dateStr = getLocalDateString(d);
       data.push({
         name: d.toLocaleDateString('en-US', { weekday: 'short' }),
         intake: Math.round(monthlyStats[dateStr] || 0),
         goal: dailyGoal
       });
     }
     return data;
  }, [monthlyStats, dailyGoal]);

  // Helper to generate calendar days
 const generateCalendarDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="rounded-lg p-2 h-24"></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = getLocalDateString(date);
      const intake = monthlyStats[dateStr] || 0;
      const fillPercentage = dailyGoal > 0 ? Math.min((intake / dailyGoal) * 100, 100) : 0;

      days.push(
        <div
          key={day}
          className="relative w-full h-24 bg-gray-200 rounded-lg overflow-hidden border-2 border-gray-300"
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-blue-400 transition-all duration-500"
            style={{ height: `${fillPercentage}%` }}
          ></div>
          <div className="relative z-10 flex flex-col h-full p-1">
            <span className="font-bold self-end text-xs">{day}</span>
            {intake > 0 && (
              <div className="mt-auto text-center">
                <span className="text-xs font-bold text-white drop-shadow-md">
                  {(intake / 1000).toFixed(1)}L
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return days;
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
           <div className="flex bg-gray-10 rounded-lg p-1">
            <button
              onClick={() => setViewMode('charts')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'charts' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              title="Charts View"
            >
              <BarChart2 size={20} />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'calendar' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              title="Calendar View"
            >
              <CalendarIcon size={20} />
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button onClick={handlePrevMonth} className="p-1 rounded-full hover:bg-gray-200">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <h3 className="text-lg font-semibold w-32 text-center">
            {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h3>
          <button onClick={handleNextMonth} className="p-1 rounded-full hover:bg-gray-200">
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      </div>

      {viewMode === 'charts' ? (
        <div className="space-y-8">
          {/* Weekly Overview */}
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm relative">
            <h4 className="text-md font-semibold mb-4 text-gray-700">Last 7 Days</h4>
            <div className={`h-64 w-full ${isGuest ? 'blur-sm' : ''}`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip 
                    cursor={{ fill: '#F3F4F6' }}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Bar dataKey="intake" fill="#60A5FA" radius={[4, 4, 0, 0]} name="Intake (ml)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {isGuest && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-70 rounded-xl">
                <Lock className="h-10 w-10 text-gray-500 mb-2" />
                <p className="text-gray-60 font-medium">Sign in to view graphs</p>
                <button
                  onClick={() => (window.location.href = '/login')}
                  className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-60 text-sm"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>

          {/* Monthly Trend */}
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm relative">
            <h4 className="text-md font-semibold mb-4 text-gray-700">Monthly Trend</h4>
            <div className={`h-64 w-full ${isGuest ? 'blur-sm' : ''}`}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip 
                     contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="intake" 
                    stroke="#3B82F6" 
                    strokeWidth={3} 
                    dot={{ r: 3, fill: '#3B82F6' }} 
                    activeDot={{ r: 6 }} 
                    name="Intake (ml)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="goal" 
                    stroke="#9CA3AF" 
                    strokeDasharray="5 5" 
                    dot={false} 
                    name="Goal"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {isGuest && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-70 rounded-xl">
                <Lock className="h-10 w-10 text-gray-500 mb-2" />
                <p className="text-gray-600 font-medium">Sign in to view graphs</p>
                <button
                  onClick={() => (window.location.href = '/login')}
                  className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="font-bold text-center text-xs text-gray-500">{day}</div>
          ))}
          {generateCalendarDays()}
        </div>
      )}
    </div>
  );
};